// Channel-related type definitions

import type { SelectableItem } from '@common/shared-ui-layer/types/selectable-list'

import type { ChannelMembershipMeta, ChannelMembershipPrice, ChannelMembershipType } from './channel-payment-setup'

export enum ChannelRoles {
  MANAGER = 'MANAGER',
  MEMBER = 'MEMBER',
}

export enum ChannelVisibility {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
}

/** GET .../channels/{channelId}/stats — role count field depends on channel visibility */
export interface IChannelStatsResponse {
  postsCount: number
  memberCount: number
  adminCount?: number
  managerCount?: number
}

export interface IChannel {
  _id?: string
  name: string
  description?: string
  slug: string
  groupId: string
  visibility: ChannelVisibility
  userRole?: ChannelRoles // Available when visibility is PRIVATE
  isAnnouncement: boolean
  isDiscoverable?: boolean
  icon?: string
  membersCount: number
  createdBy: {
    contactId: string
    fullName?: string
    avatar?: string
    slug?: string
    title?: string
  }
  createdAt: string
  updatedAt: string
  unreadCount?: number
  isHome?: boolean
  isDivider?: boolean // Support for divider items
  membershipMeta?: ChannelMembershipMeta
}

export type ChannelAccessInput = Pick<IChannel, 'visibility' | 'userRole'>

export interface SelectableChannelItem extends SelectableItem {
  channel?: IChannel
}

/** Payload item for update channel sequence API */
export interface ChannelSequenceItem {
  id: string
  channelName: string
  sequence: number
}

export interface MoveChannelPlacementInput {
  channelId: string
  targetSectionId: string | null
  prevSiblingId?: string | null
  nextSiblingId?: string | null
}

export interface MoveChannelMutationInput extends MoveChannelPlacementInput {
  targetOrderedIds: string[]
}

export interface ChannelWithMetadata extends IChannel {
  unreadCount: number
  managers: Array<{
    contactId: string
    fullName?: string
    avatar?: string
    role: string
  }>
}

export interface IChannelMember {
  _id?: string
  contactId: string
  channelId: string
  groupId: string
  userId?: string
  role: ChannelRoles
  locationId: string
  createdAt: string
  lastReadTimestamp?: string
}

export interface IChannelMemberComposit {
  _id: string
  role: ChannelRoles
  contactId: string
  fullName?: string
  avatar?: string
  joiningDate: string
  slug?: string
  bio?: string
  title?: string
  lastLogin?: string
  socials?: {
    email?: string
    phoneNumber?: string
    facebook?: string
    instagram?: string
    twitter?: string
    youtube?: string
    linkedin?: string
    emailVisibility?: string
    phoneVisibility?: string
  }
}

export interface ChannelMetadata {
  managers: {
    [channelId: string]: Array<{
      contactId: string
      fullName?: string
      avatar?: string
      role: string
      slug?: string
    }>
  }
  unreadCounts: {
    [channelId: string]: number
  }
  lastReadTimestamps: {
    [channelId: string]: Date
  }
  memberCounts: {
    [channelId: string]: number
  }
}

export interface CreateChannelMembershipInput {
  membershipType: ChannelMembershipType.PAID
  prices: ChannelMembershipPrice[]
  isLivePayment: boolean
}

export interface CreateChannelInput {
  name: string
  description?: string
  visibility: ChannelVisibility
  isAnnouncement?: boolean
  isDiscoverable?: boolean
  isDefault?: boolean
  icon?: string
  color?: string
  /** Paid membership only — omit entirely for free channels */
  membership?: CreateChannelMembershipInput
}

export interface UpdateChannelInput extends Partial<CreateChannelInput> {
  _id: string
}

export interface UpdateChannelAccessSettingsInput {
  channelId: string
  visibility: ChannelVisibility
  isDiscoverable: boolean
}

export interface JoinPaidChannelInput {
  orderId: string
  orderSourceId: string
}

export interface ChannelResponse {
  channel: IChannel
  members?: IChannelMember[]
  metadata?: ChannelMetadata
}

export interface PaginationParams {
  limit?: number
  skip?: number
  offset?: number
  pageNo?: number
  search?: string
}

export interface ChannelsState {
  currentChannel: IChannel | null
  channelList: IChannel[]
  restrictedChannelList: IChannel[]
  nonRestrictedChannelList: IChannel[]
  fetchingChannels: boolean
  fetchingChannelData: boolean
  isLoadingMetadata: boolean
  channelMetadata: ChannelMetadata
  error: any
}
